import withStyles from '@mui/styles/withStyles';
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import AddCircleIcon from '@mui/icons-material/AddCircle'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { PickersDay } from "@mui/x-date-pickers/PickersDay"
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import classNames from 'classnames'
import AddEventForm from "components/AddEventForm"
import CustomCalendarEvent from "components/CustomCalendarEvent"
import ScheduledMentorshipSession from "components/ScheduledMentorshipSession"
import Streaks from "components/Streaks"
import {filter, get, includes, map} from "lodash"
import moment from "moment"
import React, {useEffect, useState} from "react"
import {useTranslation} from "react-i18next";
import { createTheme } from '@mui/material/styles'
import CalendarIntegrationOptions from './CalendarIntegrationOptions'
import tick from "common/assets/tick.png"
import Tooltip from "@mui/material/Tooltip";

const styles = theme => ({
  expansionTitleWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  panelTitle: {
    fontWeight: 'bold',
    fontSize: 14
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
      alignItems: 'center'
    },
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start'
    },
    padding: '0px 0px 16px 0px'
  },
  deadline: {
    borderBottom: '1px solid #52B2E2'
  },
  badgeColor: {
    backgroundColor: '#DE6E49'
  },
  event: {
    color: '#DE6E49',
  },
  streakHighlight:{
    backgroundColor:'#FAF2EF',
    borderRadius:'50%'
  },
  spin: {
    animation: `$spin 1s linear infinite`,
    color: '#52B2E2',
    marginLeft: theme.spacing(1),
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" }
  },
  calendarIntegrationOptions: {
    padding: 10,
    marginBottom: 10
  },
  icon: {
    width: 22,
    height: 22,
    cursor: "pointer",
    borderRadius: 6,
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
      boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
    },
  },
});

const calendarTheme = createTheme({
  components: {
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: '#E0B334',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 0, 0, 0.5)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            color: 'rgba(0, 0, 0, 0.5)',
            '&:hover, &:focus': {
              backgroundColor: 'rgba(0, 0, 0, 0.10)'
            },
          },
        },
        today: {
          color: '#000000',
        },
      },
    },
  },
});


const MentorshipCalendar = ({classes, loadEvents, loadCalendarIntegrations, mentorshipId, events, sessions, ongoingSessions, submitEvent, profileId, cancelEvent, progress, mentorshipGroupId, saveCalendarIntegration, calendarIntegrations, unlinkCalendarIntegration}) => {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(true);
  const [isCalendarSynced, setIsCalendarSynced ] = useState(calendarIntegrations.length > 0);
  // A synced calendar whose token could not be auto-renewed is flagged by the
  // backend via needs_reconnect, camelCased to needsReconnect by the API layer.
  const [isCalendarExpired, setIsCalendarExpired] = useState(false);

  useEffect(() => {
    mentorshipId && loadEvents(mentorshipId);
    mentorshipId && loadCalendarIntegrations();
  }, [mentorshipId]);

  useEffect(() => {
    setIsCalendarSynced(calendarIntegrations.length > 0)
    setIsCalendarExpired(calendarIntegrations.some((ci) => ci.needsReconnect))
  }, [calendarIntegrations]);

  // Re-read the sync status when the tab regains focus, so an expired prompt
  // clears once the calendar has been reconnected or the nightly job has
  // renewed the token, rather than lingering until a manual page refresh.
  useEffect(() => {
    const refreshStatus = () => {
      if (document.visibilityState === "visible") {
        loadCalendarIntegrations();
      }
    };
    window.addEventListener("focus", refreshStatus);
    document.addEventListener("visibilitychange", refreshStatus);
    return () => {
      window.removeEventListener("focus", refreshStatus);
      document.removeEventListener("visibilitychange", refreshStatus);
    };
  }, [loadCalendarIntegrations]);

  const ongoingSessionsEndDate = (sessions) => map(sessions, session => moment(session.endDate).format('YYYY-MM-DD'));
  const thisMonthEventsDates = (events) => map(events, event => moment(event.scheduledTime).format('YYYY-MM-DD'));
  const thisMonthStreakDates = (streaks) => map(streaks, streak => moment(streak.timestamp).format('YYYY-MM-DD'));

  const thisMonthOngoingSessions = (ongoingSessions, selectedDate) => {
    return filter(ongoingSessions, (session) => session.endDate && moment(session.endDate).format('YYYY-MM') ===
      moment(selectedDate).format('YYYY-MM'));
  };
  const thisMonthEvents = (events, selectedDate) => {
    return filter(events, (event) => event.scheduledTime && moment(event.scheduledTime).format('YYYY-MM') ===
      moment(selectedDate).format('YYYY-MM'));
  };

  const thisDayEvents = (events, selectedDate) => {
    return filter(events, (event) => event.scheduledTime && moment(event.scheduledTime).format('YYYY-MM-DD') ===
      moment(selectedDate).format('YYYY-MM-DD'));
  };

  const thisMonthStreaks = (streaks, selectedDate) => {
    return filter(streaks, (streak) => streak.timestamp && moment(streak.timestamp).format('YYYY-MM') ===
      moment(selectedDate).format('YYYY-MM'));
  };

  const [date, changeDate] = useState(new Date());
  const [month, changeMonth] = useState(new Date());
  const [isExpanded, setisExpanded] = useState(true)

  const handleExpandPanel = (isExpanded) => setisExpanded(!isExpanded)

  const [open, setOpen] = React.useState({});

  const handleOpen = (eventType,EventId) => {
    setOpen({
      eventType,
      EventId,
      state:true
    });
  };

  const handleClose = () => {
    setOpen({
      eventType:"",
      state:false});
  };

  const handleDateChange = (date) => {
    changeDate(date);
  };
  const handleMonthChange = (date) => {
    changeMonth(date);
  };

  const endDates = ongoingSessionsEndDate(thisMonthOngoingSessions(ongoingSessions, month));
  const eventDates = thisMonthEventsDates(thisMonthEvents(events, month));
  const streakDates = thisMonthStreakDates(thisMonthStreaks(get(progress, 'quizStreaks', []), month));

  const calendar = <LocalizationProvider dateAdapter={AdapterMoment}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={calendarTheme}>
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          orientation="portrait"
          openTo="day"
          dayOfWeekFormatter={day => day}
          value={date}
          onChange={handleDateChange}
          showToolbar={false}
          onMonthChange={handleMonthChange}
          renderInput={() => null}
          renderDay={(day, selectedDays, pickersDayProps) => {
            const isInCurrentMonth = !pickersDayProps.outsideCurrentMonth;
            const isDeadline = isInCurrentMonth && includes(endDates, moment(day).format('YYYY-MM-DD'));
            const isEvent = isInCurrentMonth && includes(eventDates, moment(day).format('YYYY-MM-DD'));
            const isStreak = isInCurrentMonth && includes(streakDates, moment(day).format('YYYY-MM-DD'));

            let CustomDay = <span
              className={classNames(isDeadline && classes.deadline, isEvent && classes.event)}>
              {moment(day).format('D')}
              </span>

            return <div key={moment(day).format('YYYY-MM-DD')} className={isStreak && classes.streakHighlight}>
              <PickersDay {...pickersDayProps}>{CustomDay}</PickersDay>
            </div>
          }}
        />
      </ThemeProvider>
    </StyledEngineProvider>
  </LocalizationProvider>;

  return (
    <div className={classes.wrapper}>
      <Accordion defaultExpanded={true} expanded={isExpanded}>
        <>{loading ?
          <AccordionSummary
            expandIcon={
              <>
                {isCalendarExpired ?
                  <Tooltip title={t("calendarSyncExpired")} arrow>
                    <ErrorOutlineIcon
                      onClick={() => setLoading(false)}
                      color={"error"}
                    />
                  </Tooltip>
                  : isCalendarSynced ?
                  <img
                    src={tick}
                    alt="Loading..."
                    style={{
                      width: "20px",
                      height: "20px",
                      transform: "rotate(180deg)"
                    }}
                    onClick={() => setLoading(false)}
                  />
                  :
                  <Tooltip
                    title="Click here to sync events with your personal calendar. Changes made in your personal calendar won’t reflect in the Mentor To Go calendar."
                    arrow
                  >
                    <AutorenewIcon onClick={() => setLoading(false)} className={classes.spin} />
                  </Tooltip>
                }
                <AddCircleIcon
                  onClick={() => handleOpen("addEvent")}
                  style={{ color: "#52B2E2" }}
                />
              </>
            }
            aria-controls="panel1c-content"
            id="panel1c-header"
            classes={{ content: classes.expansionTitleWrapper }}
          >
            <div className={classes.panelTitle} onClick={() => handleExpandPanel(isExpanded)}>
              {t("calendar")}
            </div>
          </AccordionSummary>
          :
          <div className={classes.calendarIntegrationOptions}>
            <CalendarIntegrationOptions
              saveCalendarIntegration={saveCalendarIntegration}
              calendarIntegrations={calendarIntegrations}
              unlinkCalendarIntegration={unlinkCalendarIntegration}
              loadCalendarIntegrations={loadCalendarIntegrations}
            />
          </div>
      }</>
        <AccordionDetails className={classes.details}>
          <Streaks progress={progress}/>
          {calendar}
          {thisDayEvents(events, date).map(event => (
            <CustomCalendarEvent
              event={event}
              editEvent={(EventId)=>handleOpen("editEvent", EventId)}
            />
          ))}
          <ScheduledMentorshipSession
            sessions={thisMonthOngoingSessions(ongoingSessions, month)}
          />
        </AccordionDetails>
      </Accordion>

      <AddEventForm
        open={open}
        sessions={sessions}
        events={events}
        handleClose={handleClose}
        profileId={profileId}
        submitEvent={submitEvent}
        cancelEvent={cancelEvent}
        mentorshipGroupId={mentorshipGroupId}
        mentorshipId={mentorshipId}
      />
    </div>
  );
};

export default withStyles(styles)(MentorshipCalendar);
